import {
  CampaignStatusesEnum,
  CampaignUserResultMessages,
} from '@/client/campaigns';
import { FilterNamesEnum, getFiltersFromColumns } from '@/client/helpers';
import { Subjects, User, UserCampaign, UserCampaignType } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import {
  DataTable,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useUserCampaigns } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats } from '@/system-settings/enums';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getCampaignStatusOptions,
  getCampaignUserResultStatuses,
} from '@/utils/helpers/campaign.helper';
import { t } from 'i18next';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledDataTable = styled(DataTable)`
  &.p-datatable {
    th {
      &.clicked-link-icon .p-column-title:before,
      &.submitted-data-icon .p-column-title:before,
      &.email-reported-icon .p-column-title:before {
        font-family: var(--icon-font-family);
        display: inline-block;
        color: var(--black-main);
        font-size: var(--medium-font-size);
        line-height: var(--medium-line-height);
        padding-right: 2px;
      }

      &.clicked-link-icon .p-column-title:before {
        font-weight: 900;
        content: '\f245';
      }

      &.submitted-data-icon .p-column-title:before {
        font-weight: 900;
        content: '\f06a';
      }

      &.email-reported-icon .p-column-title:before {
        font-weight: 900;
        content: '\f0a1';
      }
    }
  }
`;

type UserPhishingDatatableProps = {
  user: User;
};

export const UserPhishingDatatable: React.FC<UserPhishingDatatableProps> = ({
  user,
}) => {
  const navigate = useNavigate();
  const { canUseFeature } = useFeatureFlag();
  const currentUser = useAppSelector(selectCurrentUser);

  const { skip, take, sort, apiFilters, onFilter, onSort, onPage } = useTable({
    tableName: TableNamesEnum.USER_PHISHING_LIST,
  });

  const { isLoading, userCampaigns } = useUserCampaigns({
    userId: user?.id,
    take,
    skip,
    filters: apiFilters,
    sort: sort && sort.length > 0 ? [sort.join(',')] : [],
  });

  const columns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('campaign.template.name'),
      sortable: false,
      filterable: true,
      filters: {
        placeholder: t('templates.searchName'),
        field: FilterNamesEnum.NAME,
        type: FilterTypeEnum.TEXT,
      },
      render: (row: UserCampaign) => row?.name,
    },
    {
      field: 'status',
      header: t('generic.status'),
      sortable: false,
      filterable: true,
      filters: {
        field: FilterNamesEnum.CAMPAIGNS_BY_STATUS,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('generic.status.search'),
        options: getCampaignStatusOptions(t),
      },
      render: (row: UserCampaign) => t(`campaign.status.${row?.status}`),
    },
    {
      field: 'startDate',
      header: t('campaign.launchDate'),
      sortable: false,
      filterable: false,
      render: (row: UserCampaign) =>
        row?.launchDate ? (
          <FormatDate date={row.launchDate} format={DateFormats.TIMEDATE} />
        ) : (
          t('generic.noDate')
        ),
    },
    {
      field: 'type',
      header: t('user.simulationType'),
      sortable: false,
      filterable: true,
      filters: {
        type: FilterTypeEnum.SELECT,
        options: [
          {
            label: t(`user.campaign.type.${UserCampaignType.COMPANY_WIDE}`),
            value: UserCampaignType.COMPANY_WIDE,
          },
          {
            label: t(`user.campaign.type.${UserCampaignType.INDIVIDUAL}`),
            value: UserCampaignType.INDIVIDUAL,
          },
        ],
        field: FilterNamesEnum.CAMPAIGN_TYPE,
      },
      render: (row: UserCampaign) => t(`user.campaign.type.${row?.type}`),
    },
    {
      field: 'clickedLinks',
      header: (
        <Tooltip
          position="top"
          target="th.clicked-link-icon"
          content={t('campaign.clicked')}
        />
      ),
      iconLabel: t('campaign.clicked'),
      headerClassName: 'clicked-link-icon',
      sortable: false,
      filterable: false,
      render: (row: UserCampaign) => (row?.clickedLinks ? row.clickedLinks : 0),
    },
    {
      field: 'submittedData',
      header: (
        <Tooltip
          position="top"
          target="th.submitted-data-icon"
          content={t('campaign.submitted')}
        />
      ),
      iconLabel: t('campaign.submitted'),
      headerClassName: 'submitted-data-icon',
      sortable: false,
      filterable: false,
      render: (row: UserCampaign) =>
        row?.submittedData ? row.submittedData : 0,
    },
    ...((canUseFeature(Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING)
      ? [
          {
            field: 'emailsReported',
            header: (
              <Tooltip
                position="top"
                target="th.email-reported-icon"
                content={t('campaign.email.reported')}
              />
            ),
            iconLabel: t('campaign.email.reported'),
            headerClassName: 'email-reported-icon',
            sortable: false,
            filterable: false,
            render: (row: UserCampaign) =>
              row?.emailsReported ? row.emailsReported : 0,
          },
        ]
      : []) as DataTableColumnType[]),
    {
      field: 'lastResultStatus',
      header: t('generic.lastAction'),
      className: 'no-padding',
      style: { minWidth: '150px' },
      sortable: false,
      filterable: true,
      filters: {
        type: FilterTypeEnum.MULTI_SELECT,
        options: getCampaignUserResultStatuses(
          t,
          canUseFeature(Subjects.EMAIL_OPEN_EVENTS),
        ),
        field: FilterNamesEnum.CAMPAIGN_USER_RESULT_STATUS,
      },
      render: (row: UserCampaign) => (
        <>
          {row?.lastResultStatus ? (
            <span>{t(CampaignUserResultMessages[row.lastResultStatus])}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </>
      ),
    },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns
    .filter((column) => !['emailsReported'].includes(column.field))
    .map((column) => column.field);
  const alwaysVisibleColumns = ['name'];
  //

  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <DataTableFilters
          filters={getFiltersFromColumns(columns)}
          onFilter={onFilter}
          className="flex-initial"
          tableName={TableNamesEnum.USER_PHISHING_LIST}
        />
      </FlexContainer>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={TableNamesEnum.USER_PHISHING_LIST}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );
  const onRowClick = (campaign: UserCampaign) => {
    if (isLoading) return;
    const campaignHasReport = ![
      CampaignStatusesEnum.DRAFT,
      CampaignStatusesEnum.PENDING_LAUNCH,
      CampaignStatusesEnum.FAILED_LAUNCH,
    ].includes(campaign.status);

    if (campaignHasReport) {
      navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_REPORT](campaign.id));
    } else if (currentUser?.account.isSystem) {
      navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](campaign.id));
    }
  };

  return (
    <StyledDataTable
      dataKey="id"
      data={userCampaigns?.result}
      count={userCampaigns?.count as number}
      isLoading={isLoading}
      toolbar={toolbar}
      columns={columns}
      visibleColumns={visibleColumns}
      onPage={onPage}
      rows={take}
      skip={skip}
      onSort={onSort}
      sort={sort}
      onRowClick={(event: DataTableRowClickEvent) =>
        onRowClick(event.data as UserCampaign)
      }
    />
  );
};
